// packages/web-ui/src/hooks/useCapabilities.ts
//
// Story W3-S6 — React hook bridging the vanilla `capabilities` zustand store
// to React's render cycle via `useSyncExternalStore`. Owns the polling
// lifecycle: a single shared store starts polling on first hook mount and
// stops only when the last consumer unmounts. This keeps the cadence at the
// promised 5 s regardless of how many components subscribe.
//
// Intentional design points:
//   - Single shared default store via `getDefaultCapabilitiesStore()`. Tests
//     pass an explicit `store` prop to inject an isolated store + mocked API.
//   - Reference-counted polling: every `useCapabilities()` call increments a
//     mount counter; the polling loop runs while the counter is >0.
//   - Returns the snapshot fields plus a stable `refresh` callback (force
//     `pollOnce()`), `notifyWorkspaceChanged`, and `openInSyside` for
//     convenience — wired into the same store's API client.
//
// AppShell wiring (`<HealthStatusBanner>`, `<StatusBar>`, the `<CapabilityProvider>`
// React context tree) is deferred to a follow-up integration tick.

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useStore } from 'zustand';
import type { StoreApi } from 'zustand/vanilla';
import type { Result } from '@tessa-lean/base';
import type {
  HealthResponse,
  OpenInSysideRequest,
  OpenInSysideResponse,
} from '@tessa-lean/base/api-schemas/index.js';

import {
  capabilitiesApi as defaultCapabilitiesApi,
  type CapabilitiesApi,
} from '../api/clients/capabilities.js';
import type { ApiContext, ApiError } from '../api/client.js';
import {
  type CapabilitiesSlice,
  type CapabilitySnapshot,
  createCapabilitiesStore,
} from '../state/capabilities.js';

// ─── Default store (singleton) ────────────────────────────────────────────

let defaultStore: StoreApi<CapabilitiesSlice> | null = null;
let mountCount = 0;

function defaultApiContext(): ApiContext {
  const token =
    typeof localStorage !== 'undefined' ? localStorage.getItem('tessaLeanToken') : null;
  return { baseUrl: '', token };
}

/**
 * Returns the shared singleton capabilities store. Lazily constructed on
 * first call so test harnesses that never invoke the hook don't allocate
 * the polling machinery.
 */
export function getDefaultCapabilitiesStore(): StoreApi<CapabilitiesSlice> {
  defaultStore ??= createCapabilitiesStore({
    api: defaultCapabilitiesApi,
    apiContext: defaultApiContext(),
  });
  return defaultStore;
}

/**
 * Test-only — replaces the singleton with the supplied store (or `null` to
 * force lazy reconstruction on the next access). The mount counter is
 * cleared so a fresh test starts with a single-poll lifecycle.
 */
export function _resetDefaultCapabilitiesStoreForTests(
  next: StoreApi<CapabilitiesSlice> | null = null,
): void {
  defaultStore = next;
  mountCount = 0;
}

// ─── Hook public surface ──────────────────────────────────────────────────

export interface UseCapabilitiesResult {
  /** Live capability snapshot, or `null` until the first poll resolves. */
  capabilities: CapabilitySnapshot | null;
  /** Most-recent full `HealthResponse`, or `null` until first poll resolves. */
  health: HealthResponse | null;
  /** True while the very first poll is in flight. */
  loading: boolean;
  /** Last poll error message, or `null` when the most-recent poll succeeded. */
  error: string | null;
  /** Force an immediate `/api/v1/health` re-poll. */
  refresh: () => Promise<void>;
  /**
   * Force a full `/api/v1/system/capabilities` refresh — populates
   * `capabilities.hasSyside` (which `/health` does NOT carry).
   */
  refreshFull: () => Promise<void>;
  /**
   * Notify the server of a workspace-root change and re-poll. Returns the
   * server's `refreshed` flag, or `null` when the request errored (the
   * `error` field is populated synchronously).
   */
  notifyWorkspaceChanged: (workspaceRoot: string) => Promise<boolean | null>;
  /**
   * Open the supplied workspace-relative path in the local syside IDE.
   * Errors flow through the standard `Result<T, ApiError>` envelope.
   */
  openInSyside: (
    input: OpenInSysideRequest,
  ) => Promise<Result<OpenInSysideResponse, ApiError>>;
}

export interface UseCapabilitiesOptions {
  /**
   * Inject a specific store + API for tests. When omitted the shared
   * singleton store is used, with reference-counted polling so multiple
   * `useCapabilities()` calls share one 5 s loop.
   */
  store?: StoreApi<CapabilitiesSlice>;
  /**
   * Override the API client for direct calls (`openInSyside`, ...). Defaults
   * to `defaultCapabilitiesApi`. Ignored when an explicit `store` is passed
   * — that store already owns its own API binding.
   */
  api?: CapabilitiesApi;
  /**
   * Override the API context for direct calls (used by `openInSyside`).
   * Defaults to a same-origin no-token context.
   */
  apiContext?: ApiContext;
  /**
   * When `false`, the hook does NOT start the polling loop on mount. The
   * caller is expected to drive `refresh()` manually. Defaults to `true`.
   */
  poll?: boolean;
}

/**
 * Reads the capability snapshot from the store and (by default) keeps the
 * polling loop running. Multiple components calling this hook share a single
 * polling cadence.
 *
 * Usage:
 *   const { capabilities, loading, error, refresh } = useCapabilities();
 */
export function useCapabilities(options: UseCapabilitiesOptions = {}): UseCapabilitiesResult {
  const usingSharedStore = options.store === undefined;
  const store = options.store ?? getDefaultCapabilitiesStore();

  // MED-13 — delegated to zustand's `useStore` so the subscribe/getSnapshot
  // pair is internally memoised. The pre-fix implementation hand-rolled
  // `useSyncExternalStore` with `useCallback`-wrapped getters; `useStore`
  // is equivalent + a smaller surface.
  const state = useStore(store);

  // Polling lifecycle — reference-counted on the shared store; per-store
  // start/stop on an injected (test) store.
  const localStartedRef = useRef(false);
  useEffect(() => {
    if (options.poll === false) return;

    if (usingSharedStore) {
      mountCount += 1;
      if (mountCount === 1) store.getState().startPolling();
    } else if (!localStartedRef.current) {
      localStartedRef.current = true;
      store.getState().startPolling();
    }

    return () => {
      if (options.poll === false) return;
      if (usingSharedStore) {
        mountCount = Math.max(0, mountCount - 1);
        if (mountCount === 0) store.getState().stopPolling();
      } else if (localStartedRef.current) {
        localStartedRef.current = false;
        store.getState().stopPolling();
      }
    };
  }, [store, options.poll, usingSharedStore]);

  const refresh = useCallback(() => store.getState().pollOnce(), [store]);
  const refreshFull = useCallback(() => store.getState().refreshFull(), [store]);
  const notifyWorkspaceChanged = useCallback(
    (workspaceRoot: string) => store.getState().notifyWorkspaceChanged(workspaceRoot),
    [store],
  );

  // `openInSyside` doesn't live on the slice (no state mutation needed) — wire
  // through the API client directly so consumers don't need to import the
  // client themselves.
  //
  // `defaultApiContext()` allocates a fresh `{baseUrl, token}` object every
  // call. Without memoising it the `openInSyside` callback was rebuilt every
  // render, propagating fresh identities into every consumer (e.g. the
  // FileEditor mount effect depends on `openInSyside` and would re-mount
  // CodeMirror on every parent render, driving an infinite setState loop).
  const directApi = options.api ?? defaultCapabilitiesApi;
  const directCtx = useMemo(
    () => options.apiContext ?? defaultApiContext(),
    [options.apiContext],
  );
  const openInSyside = useCallback(
    (input: OpenInSysideRequest) => directApi.openInSyside(directCtx, input),
    [directApi, directCtx],
  );

  return useMemo(
    (): UseCapabilitiesResult => ({
      capabilities: state.capabilities,
      health: state.health,
      loading: state.loading,
      error: state.error,
      refresh,
      refreshFull,
      notifyWorkspaceChanged,
      openInSyside,
    }),
    [
      state.capabilities,
      state.health,
      state.loading,
      state.error,
      refresh,
      refreshFull,
      notifyWorkspaceChanged,
      openInSyside,
    ],
  );
}
